import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Mail, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useT } from "@/lib/labels";

export const NewsletterBox = ({ variant = "home" }) => {
  const { settings } = useSite();
  const t = useT();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  if (settings.newsletter_enabled === false) return null;
  const dark = variant === "footer";
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: "" });
    try { await api.post("/newsletter/subscribe", { email, consent }); setState({ busy: false, done: true, error: "" }); }
    catch (err) { setState({ busy: false, done: false, error: errMsg(err) }); }
  };
  const id = `nl-${variant}`;
  return (
    <section data-testid={`newsletter-${variant}`} className={dark ? "mb-10 pb-10 border-b border-[#c9a227]/30" : "max-w-7xl mx-auto px-6 sm:px-8 py-16"}>
      <div className={dark ? "grid md:grid-cols-2 gap-6 items-center" : "vellum gilt p-8 sm:p-10 grid md:grid-cols-2 gap-8 items-center"}>
        <div>
          {t("newsletter.eyebrow") && <p className={`eyebrow ${dark ? "!text-[#e6bb45]" : ""}`}>{t("newsletter.eyebrow")}</p>}
          <h2 className={`font-display mt-1 ${dark ? "text-2xl text-[#f4efe2]" : "text-3xl sm:text-4xl text-[#2d1b0e]"}`}>{t("newsletter.title")}</h2>
          {t("newsletter.text") && <p className={`mt-2 ${dark ? "text-sm text-[#d9cbb0]" : "text-lg text-[#3b2c1f]"}`}>{t("newsletter.text")}</p>}
        </div>
        {state.done ? (
          <p data-testid={`newsletter-${variant}-success`} className={`flex items-start gap-2 ${dark ? "text-[#e6bb45]" : "text-[#2f6b3a]"} font-heading`}><Check className="w-5 h-5 shrink-0" /> {t("newsletter.success")}</p>
        ) : (
          <form onSubmit={submit} className="grid gap-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <label htmlFor={id} className="sr-only">Email</label>
              <input id={id} data-testid={`newsletter-${variant}-email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className={`admin-input flex-1 ${dark ? "!bg-[#2d1b0e] !text-[#f4efe2] !border-[#c9a227]/60" : ""}`} />
              <button data-testid={`newsletter-${variant}-submit`} disabled={state.busy} className="btn-illum theme-flame justify-center disabled:opacity-60"><Mail className="w-4 h-4" /> {t("newsletter.button")}</button>
            </div>
            <label className={`flex items-start gap-2 text-xs ${dark ? "text-[#d9cbb0]" : "text-[#4a3a2a]"}`}>
              <input type="checkbox" data-testid={`newsletter-${variant}-consent`} checked={consent} onChange={(e) => setConsent(e.target.checked)} required className="mt-0.5" />
              <span>{t("newsletter.consent")}{settings.legal_enabled && <> See our <Link to="/legal/privacy" className="underline">Privacy Notice</Link>.</>}</span>
            </label>
            {state.error && <p data-testid={`newsletter-${variant}-error`} className="text-sm text-[#ea580c]">{state.error}</p>}
          </form>
        )}
      </div>
    </section>
  );
};

export default function NewsletterActionPage() {
  const { action } = useParams();
  const [params] = useSearchParams();
  const [res, setRes] = useState({ loading: true });
  useEffect(() => {
    const ok = action === "confirm" || action === "unsubscribe";
    if (!ok) { setRes({ error: "Page not found" }); return; }
    api.post(`/newsletter/${action}`, { token: params.get("token") || "" }).then((r) => setRes(r.data)).catch((e) => setRes({ error: errMsg(e) }));
  }, [action, params]);
  const msg = res.loading ? "One moment…" : res.error ? res.error
    : action === "confirm" ? "Thank you! You're subscribed and will hear about new book releases." : "You've been unsubscribed and won't receive any more release emails.";
  return (
    <div data-testid={`newsletter-${action}-page`} className="max-w-xl mx-auto px-6 py-24 text-center">
      <p className="eyebrow">Book release news</p>
      <h1 data-testid="newsletter-action-message" className="font-display text-3xl mt-3">{msg}</h1>
      <Link to="/" className="btn-ghost-illum mt-10">Return home</Link>
    </div>
  );
}
